import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { path } from '@/src/utils/generalUtils/path'
import { getGtmPageCountry } from '@/src/utils/analytics/gtm/getGtmPageCountry'
import { AI_EVENT_NAME, AIEventInput, buildAIEventPayload } from '@/src/utils/analytics/ai/aiEvents'
import { logError } from '@/src/utils/logError'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useCallback } from 'react'
import useClevertapEvent from '../index'

const useClevertapForHomepage = () => {
    const { triggerEvent } = useClevertapEvent()
    const locale = useLocale()
    const pathname = usePathname()

    const buildHomepagePagePayload = useCallback(() => {
        const isFlightsPage = pathname?.includes('/flights') || false
        const page_name = isFlightsPage ? 'flights_homepage' : 'homepage'
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'NA'
        const domain = typeof window !== 'undefined' ? path(['location', 'hostname'], window) || 'NA' : 'NA'
        const page_country = getGtmPageCountry(hostname)
        const page_referrer = typeof window !== 'undefined' ? document.referrer : 'no_page_referrer'
        const page_currency = getUserSelectedCurrencyCode()?.toLowerCase() || 'sar'
        const is_logged_in = isUserSignedIn() ? 'yes' : 'no'
        const page_path = pathname || '/'
        const current_page = isFlightsPage ? 'flights_homepage' : 'homepage'

        return {
            hostname,
            domain,
            page_name,
            is_logged_in,
            page_language: locale,
            page_path,
            page_country,
            page_currency,
            current_page,
            page_referrer
        }
    }, [locale, pathname])

    const triggerHomepageLoadEvent = useCallback(
        (name: string) => {
            const finalPayload = buildHomepagePagePayload()
            console.log('[CleverTap]' + name + ' (homepage):', finalPayload)
            triggerEvent(name, finalPayload)
        },
        [triggerEvent, buildHomepagePagePayload]
    )

    const triggerHomepageUIActionEvent = useCallback(
        (customPayload: { action_name: string; action_type: string; action_value?: string; [key: string]: string | undefined }) => {
            const pagePayload = buildHomepagePagePayload()
            triggerEvent('Air_UI_Action', { ...pagePayload, ...customPayload })
        },
        [triggerEvent, buildHomepagePagePayload]
    )

    /**
     * AI-mode events on the home page (mode switch, query + intent, continue to SRP).
     * Always an `Air_UI_Action` — homepage page context (page_name, locale, …) is
     * merged in automatically alongside the normalised AI keys.
     */
    const triggerHomeAIEvent = useCallback(
        (input: AIEventInput) => {
            try {
                const pagePayload = buildHomepagePagePayload()
                triggerEvent(AI_EVENT_NAME.UI_ACTION, { ...pagePayload, ...buildAIEventPayload(input) })
            } catch (e) {
                logError('triggerHomeAIEvent', e)
            }
        },
        [triggerEvent, buildHomepagePagePayload]
    )

    return {
        triggerHomepageLoadEvent,
        triggerHomepageUIActionEvent,
        triggerHomeAIEvent
    }
}

export default useClevertapForHomepage
